import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { toIndianIsoString } from '@/shared/utils/dateTime'
import type { DealTimelineDetail, TimelineFilters, TimelineListResponse } from './types'

export const timelineKeys = {
  list: (filters: TimelineFilters) => ['deals', 'timeline', 'list', filters] as const,
  detail: (dealId: string, activityType: string) => ['deals', 'timeline', 'detail', dealId, activityType] as const,
}

function toParams(filters: TimelineFilters): Record<string, string> {
  const params: Record<string, string> = { scope: filters.scope }
  if (filters.search.trim()) params.search = filters.search.trim()
  if (filters.employee) params.assignedToUserId = filters.employee
  if (filters.stage) params.stage = filters.stage
  if (filters.outcome && filters.outcome !== 'all') params.outcome = filters.outcome
  if (filters.from) params.from = filters.from
  if (filters.to) params.to = filters.to
  return params
}

export function useDealTimeline(filters: TimelineFilters) {
  return useQuery({
    queryKey: timelineKeys.list(filters),
    queryFn: () => api.get<TimelineListResponse>('/deals/timeline', toParams(filters)),
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  })
}

export function useDealTimelineDetail(dealId: string | null, activityType: string) {
  return useQuery({
    queryKey: timelineKeys.detail(dealId || '', activityType),
    queryFn: () =>
      api.get<DealTimelineDetail>(
        `/deals/${dealId}/timeline`,
        activityType && activityType !== 'all' ? { activityType } : undefined
      ),
    enabled: !!dealId,
    placeholderData: (prev) => prev,
  })
}

/** Every timeline query, so a write from any quick action refreshes the whole page. */
export function useTimelineRefetch() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: ['deals', 'timeline'] })
}

export interface AssignableUser {
  id: string
  name: string
  email?: string
  department?: string
}

export function useSalesEmployees() {
  return useQuery({
    queryKey: ['users', 'assignable', 'leads'],
    queryFn: async () => (await api.get<{ data: AssignableUser[] }>('/users/assignable', { for: 'leads' })).data,
    staleTime: 5 * 60_000,
  })
}

// The quick actions write through the routes that already own these records:
// a deal's calls, follow-ups, histories, meetings and recordings live on the
// lead it was converted from, so that is where they are posted.

type LeadActivityPayload = Record<string, any>

const WALL_CLOCK_FIELDS = ['occurredAt', 'dueAt', 'scheduledAt']

function useLeadActivityWriter(leadId: string | null, path: (leadId: string) => string) {
  const refetch = useTimelineRefetch()
  return useMutation({
    mutationFn: async (body: LeadActivityPayload) => {
      if (!leadId) throw new Error('This deal was not created from a lead, so there is no lead to record it against.')
      const payload: LeadActivityPayload = { ...body }
      for (const field of WALL_CLOCK_FIELDS) {
        if (typeof payload[field] === 'string' && payload[field]) {
          payload[field] = toIndianIsoString(payload[field])
        }
      }
      return api.post(path(leadId), payload, { skipErrorToast: true })
    },
    onSuccess: refetch,
  })
}

export const useCallLogWriter = (leadId: string | null) =>
  useLeadActivityWriter(leadId, (id) => `/leads/${id}/call-logs`)

export const useFollowupWriter = (leadId: string | null) =>
  useLeadActivityWriter(leadId, (id) => `/leads/${id}/followups`)

export const useCallHistoryWriter = (leadId: string | null) =>
  useLeadActivityWriter(leadId, (id) => `/leads/${id}/call-history`)

export const useMeetingWriter = (leadId: string | null) =>
  useLeadActivityWriter(leadId, (id) => `/leads/${id}/meetings`)

/** A note on the deal itself, for deals that never came from a lead. */
export function useDealNoteWriter(dealId: string | null) {
  const refetch = useTimelineRefetch()
  return useMutation({
    mutationFn: async (content: string) => {
      if (!dealId) throw new Error('Select a deal first.')
      return api.post('/comments', { entityType: 'deal', entityId: dealId, content }, { skipErrorToast: true })
    },
    onSuccess: refetch,
  })
}

export async function uploadLeadRecording(leadId: string, file: File): Promise<void> {
  const form = new FormData()
  form.append('file', file)
  await api.post(`/leads/${leadId}/call-recordings`, form, {
    headers: { 'Content-Type': null },
    skipErrorToast: true,
  })
}
